import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useHVAConversation } from './HVARealEstateConversationContext';
import HVARealEstateWidget from './HVARealEstateWidget';
import { HVARealEstateModalProps, FeedbackMessage } from './HVARealEstateData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import img1 from 'assets/img/spot-illustrations/pisomadrid.jpg';
import img2 from 'assets/img/spot-illustrations/aptbarcelona.jpg';
import img3 from 'assets/img/spot-illustrations/masgerona.jpg';

// JSON de propiedades
const properties = {
  ABC123: {
    id: 'ABC123',
    bedrooms: 3,
    bathrooms: 2,
    location: 'Madrid',
    size: '165 m2',
    description: 'Piso moderno en zona céntrica.',
    photo: img1
  },
  XYZ789: {
    id: 'XYZ789',
    bedrooms: 2,
    bathrooms: 1,
    location: 'Barcelona',
    size: '125 m2',
    description: 'Apartamento en primera línea de playa.',
    photo: img2
  },
  FRL683: {
    id: 'FRL683',
    bedrooms: 5,
    bathrooms: 5,
    location: 'Gerona',
    size: '488 m2',
    description: 'Masía magníficamente restaurada.',
    photo: img3
  }
};

const HVARealEstateModalContent: React.FC<HVARealEstateModalProps> = ({
  show,
  onHide,
  agentCode,
  agentName,
  agentImage,
  onError,
  customer,
  dni,
  firstMessage,
  prompt,
  voice,
  language
}) => {
  const {
    setClientInfo,
    isNifValidated,
    requestTransfer,
    setRequestTransfer,
    requestClose,
    setRequestClose,
    setIsNifValidated
  } = useHVAConversation();

  // Inicializar clientInfo cuando se montan los props
  useEffect(() => {
    if (dni && customer) {
      setClientInfo({
        nif: dni,
        name: customer
      });
    }
  }, [dni, customer, setClientInfo]);

  const [formData, setFormData] = useState({
    nif: dni || '',
    name: customer || ''
  });

  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );

  // Función para añadir mensajes de feedback
  const addFeedbackMessage = (
    message: Omit<FeedbackMessage, 'id' | 'timestamp'>
  ) => {
    setFeedbackMessages(prev => [
      {
        ...message,
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date()
      },
      ...prev
    ]); // Añadimos el nuevo mensaje al principio del array
  };

  // Función para resetear el estado
  const resetState = () => {
    setFormData({
      nif: dni || '',
      name: customer || ''
    });
    setFeedbackMessages([]);
    setIsNifValidated(false);
    setRequestTransfer(false);
    setRequestClose(false);
    setClientInfo({
      nif: dni || '',
      name: customer || ''
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setClientInfo({
      nif: formData.nif,
      name: formData.name
    });
    // Reseteamos la validación del NIF cuando se actualiza
    setIsNifValidated(false);

    // Añadimos un mensaje de feedback para indicar la actualización
    setFeedbackMessages(prev => [
      {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'nif',
        message: 'Actualización de datos del cliente',
        details: `Nuevo NIF: ${formData.nif}\nNombre: ${formData.name}`,
        success: true
      },
      ...prev
    ]);
  };

  const handleCloseModal = () => {
    resetState();
    onHide();
  };

  const handleTransfer = async () => {
    try {
      // Resetear todos los estados
      resetState();

      // Añadir mensaje de feedback sobre la transferencia
      const newFeedbackMessage = {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'close',
        message: 'Llamada finalizada',
        details: `NIF: ${formData.nif} - Nombre: ${formData.name}`,
        success: true
      } as FeedbackMessage;

      setFeedbackMessages(messages => [newFeedbackMessage, ...messages]);
    } catch (error) {
      console.error('Error al transferir la llamada:', error);
      onError?.(error as Error);
    }
  };

  const renderTransferAlert = () => {
    if (!requestTransfer) return null;

    return (
      <div className="d-flex align-items-center fs-9 pt-2">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        <div className="flex-grow-1">Request to Transfer Call</div>
        <Button variant="warning" size="sm" onClick={handleTransfer}>
          Transfer Call
        </Button>
      </div>
    );
  };

  const renderCloseAlert = () => {
    if (!requestClose) return null;

    return (
      <div className="d-flex align-items-center fs-9 pt-2">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        <div className="flex-grow-1">Request to End Call</div>
        <Button variant="warning" size="sm" onClick={handleTransfer}>
          Hang Up
        </Button>
      </div>
    );
  };

  const renderFeedbackPanel = () => {
    return (
      <>
        <h5 className="pb-0 pt-4">{`${agentName}'s feedback`}</h5>
        <p className="fs-9 lh-sm">
          The panel below displays requests for information or tool usage made
          during the conversation.
        </p>
        <div
          className="p-3 pt-0 border rounded scrollbar bg-body-emphasis shadow-sm"
          style={{
            height: '200px',
            overflowY: 'auto',
            scrollBehavior: 'smooth'
          }}
        >
          {renderCloseAlert()}
          {renderTransferAlert()}

          {/* Mensajes de feedback de las tools */}
          <div className="mt-3">
            {feedbackMessages.map(msg => (
              <div
                key={msg.id}
                className={`alert ${
                  msg.success ? 'bg-body' : 'alert-warning'
                } mb-2 py-2 px-3`}
                role="alert"
              >
                <div className="d-flex flex-column">
                  <small className="text-muted fs-10">
                    {msg.timestamp.toLocaleTimeString()}
                  </small>
                  <div className="fs-9 text-primary">{msg.message}</div>
                  {msg.details && (
                    <small className="mt-1 text-muted fs-10 lh-1">
                      {msg.details}
                    </small>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderPropertyDetails = () => {
    return (
      <div className="property-details d-flex flex-wrap gap-3 mt-1 p-0">
        {Object.values(properties).map(property => (
          <div
            key={property.id}
            className="property-card p-0 border rounded shadow-sm bg-body d-flex flex-row"
            style={{ width: '342px' }}
          >
            {/* Imagen a la izquierda */}
            <img
              src={property.photo}
              alt={property.location}
              className="img-fluid rounded"
              style={{
                height: '80px',
                objectFit: 'cover',
                width: '80px',
                marginRight: '10px'
              }}
            />
            {/* Descripción a la derecha */}
            <div className="property-info pt-2">
              <h6 className="text-primary">{property.location}</h6>
              <p className="mb-1 fs-9 lh-1">
                <strong>ID: {property.id}</strong>
              </p>
              <p className="mb-0 fs-10 lh-1">{property.description}</p>
              <p className="mb-0 fs-10 lh-1">
                {property.bedrooms} habitaciones · {property.bathrooms} baños
              </p>
              <p className="mb-0 fs-10 lh-1">{property.size || 'N/A'}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      contentClassName="border-0"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-body-emphasis">{agentName}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 pe-3 bg-body">
        <Row className="g-0">
          <h3 className="pb-0 pt-4 px-6">POC scenario</h3>
          <p className="fs-8 lh-sm px-6 mb-0">
            This Proof of Concept (POC) demonstrates the interaction between the
            agent, the customer, and the system. The agent starts the call
            without any prior information, which is provided incrementally as
            needed during the interaction. This approach ensures that the agent
            does not have access to confidential information unless all security
            protocols have been successfully completed beforehand.
          </p>
          <h5 className="px-6 pe-4 pb-0 pt-3">The Real Estate offering</h5>
          <div className="px-6 pe-4 d-flex justify-content-center align-items-center">
            {renderPropertyDetails()}
          </div>
          {/* Columna izquierda: Info inicial y solicitudes del agente durante la conversación */}
          <Col md={4} className="p-4 px-6 pe-0">
            <h5 className="pb-0 pt-0">Data to verify during the call</h5>
            <p className="fs-9 lh-sm">
              The NIF (Tax Identification Number) is not known to the agent. The
              agent will only collect the information provided by the client and
              request us to verify it.
            </p>
            <Form onSubmit={handleSubmit} className="w-100">
              <div className="d-flex flex-column gap-2">
                {/* Primera fila - Nombre completo */}
                <Form.Floating>
                  <Form.Control
                    className="shadow-sm"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full name"
                  />
                  <label>Full name</label>
                </Form.Floating>

                {/* Segunda fila - Tipo de préstamo y botón */}
                <div className="d-flex gap-2">
                  <Form.Floating className="flex-grow-1">
                    <Form.Control
                      className="shadow-sm"
                      type="text"
                      name="nif"
                      value={formData.nif}
                      onChange={handleInputChange}
                      placeholder="NIF"
                      isValid={isNifValidated}
                    />
                    <label>NIF</label>
                  </Form.Floating>

                  <Button
                    type="submit"
                    variant="primary"
                    style={{ minWidth: '100px' }}
                  >
                    Update
                  </Button>
                </div>
              </div>
            </Form>
            {renderFeedbackPanel()}
          </Col>
          {/* Columna centro: Imagen */}
          <Col md={2} className="d-flex p-4 pe-0 pb-0 align-items-end">
            {agentImage && (
              <div className="text-center mb-2">
                <img
                  src={agentImage}
                  alt={agentName}
                  className="img-fluid rounded"
                  style={{
                    width: '100%',
                    minWidth: '163px',
                    maxWidth: '163px',
                    height: 'auto'
                  }}
                />
              </div>
            )}
          </Col>
          {/* Columna derecha: Datos dinámicos, widget de Conversación e información dinámica */}
          <Col md={6} className="p-4">
            <h5 className="pb-0 pt-0">Call with {agentName}</h5>
            <p className="fs-9 lh-sm">
              Press the button below to start a conversation with the agent. For
              the Proof of Concept (POC) to function correctly, it is necessary
              to have selected a NIF beforehand for its verification during the
              conversation.
            </p>
            <HVARealEstateWidget
              agentId={agentCode}
              agentName={agentName}
              onError={onError}
              onFeedback={addFeedbackMessage}
              firstMessage={firstMessage}
              prompt={prompt}
              voice={voice}
              language={language}
            />
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleCloseModal}>
          Close and reset POC
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HVARealEstateModalContent;
